import { useEffect, useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { publicApi } from '@/lib/api';
import { Star } from 'lucide-react';
import { useLanguage } from '@/lib/i18n';

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const { t } = useLanguage();

  useEffect(() => {
    publicApi.getReviews().then(r => setReviews(r.data)).catch(() => {});
  }, []);

  const avgRating = reviews.length > 0 ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1) : '0';

  return (
    <div className="min-h-screen bg-cream" data-testid="reviews-page">
      <Navbar />
      <div className="pt-28 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
          <span className="text-sm font-medium tracking-wide uppercase text-garden-500 mb-2 block">{t('reviews.sectionLabel')}</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-garden-800 mb-4">
            {t('reviews.title')}
          </h1>
          <p className="text-base md:text-lg text-charcoal/70 max-w-2xl">
            {t('reviews.description')}
          </p>
        </div>

        {/* Average Rating */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
          <div className="bg-white rounded-3xl p-8 border border-garden-800/5 shadow-sm inline-flex items-center gap-6">
            <div className="text-center">
              <span className="text-5xl font-heading font-bold text-garden-800">{avgRating}</span>
              <span className="text-lg text-charcoal/50">/5</span>
            </div>
            <div>
              <div className="flex gap-1 mb-1">
                {[1, 2, 3, 4, 5].map((s) => (
                  <Star key={s} size={20} className={s <= Math.round(Number(avgRating)) ? 'text-sunny fill-sunny' : 'text-gray-300'} />
                ))}
              </div>
              <p className="text-sm text-charcoal/60">{t('reviews.outOf')}</p>
            </div>
          </div>
        </div>

        {/* Reviews Grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((review, i) => (
              <div
                key={review.id}
                className={`bg-white rounded-2xl p-6 border border-garden-800/5 shadow-sm hover:shadow-md transition-shadow opacity-0 animate-fade-up`}
                style={{ animationDelay: `${i * 0.1}s` }}
                data-testid={`review-item-${review.id}`}
              >
                <div className="flex gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star key={s} size={16} className={s <= review.rating ? 'text-sunny fill-sunny' : 'text-gray-300'} />
                  ))}
                </div>
                <p className="text-charcoal/80 text-sm leading-relaxed mb-4">"{review.text}"</p>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-garden-800 text-sm">{review.name}</span>
                  <span className="text-xs text-charcoal/40">{review.created_at}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
